// The signed-in "you" pages (profile, edit profile, privacy, notifications,
// account, connections, referrals…) are drawn with the club dashboard's kit
// (../manage/ui.js + club.css), so they look and behave like the Events pages:
// a slim bar with a back chevron, a big title, sections underneath.
//
// Pieces here are only what those pages share on top of the kit: the page
// shell, where "back" goes (web, app tab, or a native app screen), rows for
// settings lists and the unsaved-changes bar of the edit forms.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, Switch, cn } from "../manage/ui";
import { ClubToaster } from "../manage/toast";
import { isTabBarOverlay } from "../../components/layout/appTabBar";

// Messages to the app go through the bridge when it's there (the app also
// shims window.postMessage onto it).
export const postToApp = (message) => {
  if (window.ReactNativeWebView?.postMessage) window.ReactNativeWebView.postMessage(message);
  else window.postMessage(message, "*");
};

// The page runs in the app as a native screen of its own (Privacy,
// Notifications, Account, Connections…): the app sets `dedicatedScreen` there.
export const isDedicatedAppScreen = () => Boolean(window.isInApp && window.dedicatedScreen);

// Older app builds open Edit profile as its own screen without the flag, but
// only tab webviews carry the tab-bar contract, so its absence means the same.
export const isAppScreenOutsideTabs = () => Boolean(window.isInApp && (window.dedicatedScreen || !isTabBarOverlay()));

// Where "back" goes. A native app screen is closed with the message that
// screen already listens for (so app builds in the stores keep working);
// everywhere else — the website and the app's Profile tab — it's web history,
// or `fallback` when the page was opened directly. Never `{type:"goBack"}` in
// a tab: the app's tab navigator would switch tabs.
export const useAccountBack = ({ fallback = "/profile", appMessage, inAppScreen = isDedicatedAppScreen } = {}) => {
  const navigate = useNavigate();
  return useCallback(() => {
    if (appMessage && inAppScreen()) {
      postToApp(appMessage);
      return;
    }
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [appMessage, fallback, inAppScreen, navigate]);
};

export const useScrolled = (threshold = 4) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

export const BackButton = ({ onClick, className = "" }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t("back", { defaultValue: "Back" })}
      onClick={onClick}
      className={cn("tw--ml-1.5 tw-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors hover:tw-bg-[var(--fill)] active:tw-bg-[var(--fill-strong)]", className)}
    >
      <ChevronLeft className="tw-size-6" />
    </button>
  );
};

// The bar at the top of a page: back on the left, actions on the right. It
// stays pinned on phones (see-through at the top, solid with a hairline once
// the page scrolls under it, the page title fading in) — the Events dashboard
// bar in the app. On wider screens the site header is above it, so it scrolls
// with the page.
export const AccountBar = ({ onBack, left, title, actions, maxWidth = "tw-max-w-[680px]", className = "" }) => {
  const scrolled = useScrolled();
  const showTitle = useScrolled(64);
  // A native app screen with a floating back of its own (the other-user
  // profile) hides it once the page says it draws one.
  const hasBack = left === undefined && Boolean(onBack);
  useEffect(() => {
    if (hasBack && isDedicatedAppScreen()) postToApp(JSON.stringify({ type: "webBackButton" }));
  }, [hasBack]);
  return (
    <header
      className={cn(
        "tw-sticky tw-top-0 tw-z-30 tw-border-b tw-transition-colors sm:tw-static sm:tw-border-transparent sm:tw-bg-transparent",
        "tw-bg-background",
        scrolled ? "tw-border-[var(--hairline)]" : "tw-border-transparent",
        className
      )}
    >
      <div className={cn("tw-relative tw-mx-auto tw-flex tw-h-14 tw-items-center tw-gap-2 tw-px-4 md:tw-px-6", maxWidth)}>
        {left !== undefined ? left : onBack ? <BackButton onClick={onBack} /> : null}
        {title && (
          <span
            aria-hidden={!showTitle}
            className={cn(
              "tw-pointer-events-none tw-absolute tw-inset-x-16 tw-truncate tw-text-center tw-text-[15px] tw-font-semibold tw-transition-opacity tw-duration-150 sm:tw-hidden",
              showTitle ? "tw-opacity-100" : "tw-opacity-0"
            )}
          >
            {title}
          </span>
        )}
        <div className="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-1">{actions}</div>
      </div>
    </header>
  );
};

const WIDTHS = { narrow: "tw-max-w-[680px]", default: "tw-max-w-[820px]", wide: "tw-max-w-[1080px]" };

// Page shell. `back` = { fallback, appMessage, inAppScreen } or a function;
// `bar={false}` drops the bar (a page that draws its own, like the profile).
export const AccountPage = ({
  title,
  description,
  actions,
  barActions,
  back,
  bar = true,
  width = "narrow",
  hideTitle = false,
  footer,
  className = "",
  contentClassName = "",
  children,
}) => {
  const backFn = useAccountBack(typeof back === "object" && back ? back : {});
  const onBack = typeof back === "function" ? back : back ? backFn : undefined;
  const maxWidth = WIDTHS[width] || width;
  return (
    // sm:pt — the site header (86px, fixed) sits above these pages on wider
    // screens; phones and the app get the bar instead.
    <div className={cn("club-admin tw-relative tw-min-h-screen tw-bg-background sm:tw-pt-[86px]", className)}>
      <ClubToaster />
      {bar && <AccountBar onBack={onBack} title={title} actions={barActions} maxWidth={maxWidth} />}
      <main className={cn("tw-mx-auto tw-w-full tw-px-4 tw-pb-16 md:tw-px-6", bar ? "tw-pt-2" : "tw-pt-6", maxWidth, contentClassName)}>
        {!hideTitle && (title || actions) && (
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
            <div className="tw-min-w-0">
              {title && <h1 className="cs-title tw-break-words">{title}</h1>}
              {description && <p className="tw-mt-1.5 tw-max-w-xl tw-text-[15px] tw-text-muted-foreground">{description}</p>}
            </div>
            {actions && <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </main>
      {footer}
    </div>
  );
};

// ---------------------------------------------------------------- Settings
// A group of rows in a translucent fill with hairlines between them (Luma's
// option list), e.g. the Privacy toggles.

export const SettingsGroup = ({ children, className = "" }) => (
  <div className={cn("tw-overflow-hidden tw-bg-[var(--fill)] tw-rounded-[var(--r-card)] [&>*+*]:tw-border-t [&>*+*]:tw-border-[var(--hairline)]", className)}>{children}</div>
);

// One row: optional icon, label + description, and a control on the right —
// a switch (`checked` / `onCheckedChange`), a value, or a chevron when the row
// is a link (`onClick`).
export const SettingsRow = ({ icon: Icon, label, description, checked, onCheckedChange, disabled, value, onClick, right, danger = false, className = "" }) => {
  const isSwitch = typeof onCheckedChange === "function";
  const Tag = onClick && !isSwitch ? "button" : isSwitch ? "label" : "div";
  return (
    <Tag
      type={Tag === "button" ? "button" : undefined}
      onClick={Tag === "button" ? onClick : undefined}
      className={cn(
        "tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-left",
        // Hover only where there is one: a tap on a phone would leave it stuck.
        (Tag === "button" || isSwitch) && !disabled && "tw-cursor-pointer tw-transition-colors active:tw-bg-[var(--fill)] [@media(hover:hover)]:hover:tw-bg-[var(--fill)]",
        disabled && "tw-opacity-60",
        className
      )}
    >
      {Icon && <Icon className={cn("tw-size-[18px] tw-shrink-0", danger ? "tw-text-destructive" : "tw-text-muted-foreground")} />}
      <span className="tw-min-w-0 tw-flex-1">
        <span className={cn("tw-block tw-text-[15px] tw-font-medium", danger && "tw-text-destructive")}>{label}</span>
        {description && <span className="tw-mt-0.5 tw-block tw-text-[13px] tw-leading-snug tw-text-muted-foreground">{description}</span>}
      </span>
      {value != null && <span className="tw-shrink-0 tw-text-[15px] tw-text-muted-foreground">{value}</span>}
      {right}
      {isSwitch && <Switch checked={!!checked} onCheckedChange={onCheckedChange} disabled={disabled} />}
      {Tag === "button" && <ChevronRight className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />}
    </Tag>
  );
};

// ---------------------------------------------------------------- Save bar
// Floating bar for edit forms: shows once something changed, sits above the
// app's tab bar, Discard + Save.

export const SaveBar = ({ visible, onSave, onDiscard, saving = false, saveLabel, discardLabel, message }) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.18 }}
          className="app-floating-card tw-fixed tw-inset-x-0 tw-bottom-[calc(1rem+var(--app-tabbar-inset,0px))] tw-z-40 tw-flex tw-justify-center tw-px-4"
        >
          <div className="cs-popover-shadow tw-flex tw-w-full tw-max-w-[520px] tw-items-center tw-gap-2 tw-bg-popover tw-py-2 tw-pl-4 tw-pr-2 tw-rounded-[var(--r-xl)]">
            <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[15px] tw-font-medium">{message || t("account_unsaved_changes", { defaultValue: "Unsaved changes" })}</span>
            {onDiscard && (
              <Button variant="light" onClick={onDiscard} disabled={saving}>
                {discardLabel || t("club_discard", { defaultValue: "Discard" })}
              </Button>
            )}
            <Button onClick={onSave} loading={saving}>
              {saveLabel || t("save", { defaultValue: "Save" })}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Small caps-free label above a group of rows.
export const GroupLabel = ({ children, className = "" }) => <p className={cn("tw-mb-2 tw-px-1 tw-text-sm tw-font-medium tw-text-muted-foreground", className)}>{children}</p>;
